import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Save, Loader2, Upload, Link, Image as ImageIcon, GripVertical, Sparkles, Mic, Camera, Video, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['breakfast', 'lunch', 'dinner_main', 'dinner_secondary', 'snack'];

export default function RecipeFormPage() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { recipeId } = useParams();
  const isEdit = !!recipeId && recipeId !== 'new';
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [importingUrl, setImportingUrl] = useState(false);
  const [importUrlValue, setImportUrlValue] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showAudioDialog, setShowAudioDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [importVideoValue, setImportVideoValue] = useState('');
  const [importingVideo, setImportingVideo] = useState(false);
  const [importingAudio, setImportingAudio] = useState(false);
  const [importingPhoto, setImportingPhoto] = useState(false);
  const [photoSource, setPhotoSource] = useState(null); // 'upload' | 'camera'
  const [photoQueue, setPhotoQueue] = useState([]); // [{ file, url }] staged before analyzing
  const [audioMode, setAudioMode] = useState('upload'); // 'upload' | 'record'
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [micError, setMicError] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioFileInputRef = useRef(null);
  const photoFileInputRef = useRef(null);
  const photoCaptureInputRef = useRef(null);
  const [imageMode, setImageMode] = useState('upload'); // 'url' or 'upload'
  // From-ingredients (two-step) + macro estimation
  const [showIngredientsDialog, setShowIngredientsDialog] = useState(false);
  const [ingredientList, setIngredientList] = useState(['']);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [creatingMultiple, setCreatingMultiple] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [generatingFromIng, setGeneratingFromIng] = useState(false);
  const [estimatingMacros, setEstimatingMacros] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    category: '', // Keep for compatibility during save if needed, but we'll use categories
    categories: [], // New multi-category field
    food_category: '', // Food category id for menu variety
    max_repetitions: 1,
    prep_time: '',
    servings: '', // number of servings/people
    calories: '', // Manual calories (PER SERVING) - empty string means use AI
    protein_g: '',
    carbs_g: '',
    fat_g: '',
    fiber_g: '',
    ingredients: [],
    preparation: [], // Step-by-step instructions
    allowed_days: [], // Day of week constraints (0=Monday)
  });
  const [constraintType, setConstraintType] = useState('max_repetitions'); // 'max_repetitions' or 'specific_days'
  const [savedMaxRep, setSavedMaxRep] = useState(1);
  const [adminConfig, setAdminConfig] = useState(null);
  const [foodCategories, setFoodCategories] = useState([]);

  useEffect(() => {
    if (isEdit) {
      fetchRecipe();
    }
    fetchPublicConfig();
    fetchFoodCategories();
  }, [recipeId]);

  const fetchPublicConfig = async () => {
    try {
      const response = await fetch(`${API}/config/public`, { credentials: 'include' });
      if (response.ok) {
        setAdminConfig(await response.json());
      }
    } catch (error) {
      console.error('Error fetching public config:', error);
    }
  };

  const fetchFoodCategories = async () => {
    try {
      const response = await fetch(`${API}/food-categories`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFoodCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching food categories:', error);
    }
  };

  const fetchRecipe = async () => {
    try {
      const response = await fetch(`${API}/recipes/${recipeId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          name: data.name || '',
          description: data.description || '',
          image: data.image || '',
          category: data.category || '',
          max_repetitions: data.max_repetitions || 1,
          prep_time: data.prep_time || '',
          servings: data.servings ?? '',
          calories: data.calories || '', // Keep empty if no calories (for AI estimation)
          protein_g: data.protein_g ?? '',
          carbs_g: data.carbs_g ?? '',
          fat_g: data.fat_g ?? '',
          fiber_g: data.fiber_g ?? '',
          ingredients: data.ingredients || [],
          preparation: data.preparation || [],
          allowed_days: data.allowed_days || [],
          categories: data.categories || (data.category ? [data.category] : []),
          food_category: data.food_category || '',
        });

        // Ensure image has the correct prefix if it's a relative path
        if (data.image && data.image.startsWith('/') && !data.image.startsWith('http')) {
          setFormData(prev => ({ ...prev, image: `${BACKEND_URL}${data.image}` }));
        }
        if (data.allowed_days && data.allowed_days.length > 0) {
          setConstraintType('specific_days');
        }
        // Check if image is from upload
        if (data.image && data.image.startsWith('/api/uploads/')) {
          setImageMode('upload');
        }
      } else {
        toast.error(t('errors.notFound'));
        navigate('/recipes');
      }
    } catch (error) {
      console.error('Error fetching recipe:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error(language === 'es' ? 'El nombre es requerido' : 'Name is required');
      return;
    }
    if (formData.categories.length === 0) {
      toast.error(language === 'es' ? 'Selecciona al menos una categoría' : 'Select at least one category');
      return;
    }

    if (constraintType === 'specific_days' && formData.allowed_days.length === 0) {
      toast.error(language === 'es' ? 'Selecciona al menos un día' : 'Select at least one day');
      return;
    }

    setSaving(true);

    try {
      const url = isEdit ? `${API}/recipes/${recipeId}` : `${API}/recipes`;
      const method = isEdit ? 'PUT' : 'POST';

      // Clean up preparation steps - ensure proper numbering
      const cleanedPreparation = formData.preparation
        .filter(step => step.instruction.trim())
        .map((step, index) => ({
          step_number: index + 1,
          instruction: step.instruction.trim()
        }));

      const payload = {
        ...formData,
        prep_time: formData.prep_time ? parseInt(formData.prep_time) : null,
        servings: formData.servings !== '' ? parseInt(formData.servings) : null,
        // calories: send as number or null (empty string becomes null for AI estimation)
        calories: formData.calories !== '' ? parseInt(formData.calories) : null,
        protein_g: formData.protein_g !== '' ? parseFloat(formData.protein_g) : null,
        carbs_g: formData.carbs_g !== '' ? parseFloat(formData.carbs_g) : null,
        fat_g: formData.fat_g !== '' ? parseFloat(formData.fat_g) : null,
        fiber_g: formData.fiber_g !== '' ? parseFloat(formData.fiber_g) : null,
        ingredients: formData.ingredients.filter(ing => ing.name.trim()),
        preparation: cleanedPreparation,
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(t('success.saved'));
        navigate(`/recipes/${data.recipe_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error(t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'es'
        ? 'Tipo de archivo no válido. Usa JPEG, PNG, WebP o GIF'
        : 'Invalid file type. Use JPEG, PNG, WebP or GIF');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'es'
        ? 'Archivo muy grande. Máximo 5MB'
        : 'File too large. Maximum 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API}/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload,
      });

      if (response.ok) {
        const data = await response.json();
        // Use full URL for the image
        const imageUrl = `${BACKEND_URL}${data.url}`;
        setFormData({ ...formData, image: imageUrl });
        toast.success(language === 'es' ? 'Imagen subida' : 'Image uploaded');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(t('errors.generic'));
    } finally {
      setUploadingImage(false);
    }
  };

  // Recipe Import from YouTube video
  const handleImportFromVideo = async () => {
    if (!importVideoValue.trim()) return;

    setImportingVideo(true);
    try {
      const response = await fetch(`${API}/recipes/import-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: importVideoValue }),
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          name: data.name || prev.name,
          description: data.description || prev.description,
          ingredients: data.ingredients?.length ? data.ingredients : prev.ingredients,
          preparation: data.preparation?.length ? data.preparation : prev.preparation,
          prep_time: data.prep_time || prev.prep_time,
          servings: data.servings ?? prev.servings,
          calories: data.calories || prev.calories,
          category: data.category || prev.category,
        }));
        setShowVideoDialog(false);
        setImportVideoValue('');
        toast.success(language === 'es' ? 'Receta importada correctamente' : 'Recipe imported successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error importing recipe from video:', error);
      toast.error(t('errors.generic'));
    } finally {
      setImportingVideo(false);
    }
  };

  // Recipe Import from URL
  const handleImportFromUrl = async () => {
    if (!importUrlValue.trim()) return;

    setImportingUrl(true);
    try {
      const response = await fetch(`${API}/recipes/import-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: importUrlValue }),
      });

      if (response.ok) {
        const data = await response.json();
        setFormData({
          ...formData,
          name: data.name || '',
          description: data.description || '',
          prep_time: data.prep_time || '',
          servings: data.servings ?? '',
          ingredients: data.ingredients || [],
          preparation: data.preparation || [],
          calories: data.calories || '',
          category: data.category || formData.category, // Keep current category if not detected
        });
        setShowImportDialog(false);
        setImportUrlValue('');
        toast.success(language === 'es' ? 'Receta importada correctamente' : 'Recipe imported successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error importing recipe:', error);
      toast.error(t('errors.generic'));
    } finally {
      setImportingUrl(false);
    }
  };

  // ---- Generate from ingredients (two-step) ----
  const updateIngredientItem = (i, val) => setIngredientList(list => list.map((x, idx) => (idx === i ? val : x)));
  const addIngredientItem = () => setIngredientList(list => [...list, '']);
  const removeIngredientItem = (i) => setIngredientList(list => (list.length > 1 ? list.filter((_, idx) => idx !== i) : list));

  const handleSuggestFromIngredients = async () => {
    const ingredients = ingredientList.map(i => i.trim()).filter(Boolean);
    if (ingredients.length === 0) {
      toast.error(t('recipes.fromIngredients.noIngredients'));
      return;
    }
    setSuggesting(true);
    setSuggestions([]);
    setSelectedSuggestions([]);
    try {
      const response = await fetch(`${API}/recipes/suggest-from-ingredients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ingredients }),
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('recipes.fromIngredients.error'));
      }
    } catch {
      toast.error(t('recipes.fromIngredients.error'));
    } finally {
      setSuggesting(false);
    }
  };

  const handleGenerateFromIngredients = async (dish) => {
    const ingredients = ingredientList.map(i => i.trim()).filter(Boolean);
    setGeneratingFromIng(true);
    try {
      const response = await fetch(`${API}/recipes/generate-from-ingredients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ingredients, dish_name: dish?.name, meal_type: dish?.category }),
      });
      if (response.ok) {
        const data = await response.json();
        const cat = data.category;
        const validCat = cat && CATEGORIES.includes(cat);
        setFormData(prev => ({
          ...prev,
          name: data.name || '',
          description: data.description || '',
          prep_time: data.prep_time || '',
          servings: data.servings ?? '',
          ingredients: data.ingredients || [],
          preparation: data.preparation || [],
          calories: data.calories ?? '',
          protein_g: data.protein_g ?? '',
          carbs_g: data.carbs_g ?? '',
          fat_g: data.fat_g ?? '',
          fiber_g: data.fiber_g ?? '',
          categories: validCat ? [cat] : prev.categories,
          category: validCat ? cat : prev.category,
        }));
        setShowIngredientsDialog(false);
        setSuggestions([]);
        setIngredientList(['']);
        toast.success(language === 'es' ? 'Receta generada. Revísala y guárdala.' : 'Recipe generated. Review and save.');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('recipes.fromIngredients.error'));
      }
    } catch {
      toast.error(t('recipes.fromIngredients.error'));
    } finally {
      setGeneratingFromIng(false);
    }
  };

  const toggleSuggestionSelected = (i) => {
    setSelectedSuggestions(prev => (prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]));
  };

  // Build a complete recipe payload from a generated dish (used for bulk direct-create)
  const buildRecipePayloadFromGenerated = (data, dish) => {
    const cat = (data.category && CATEGORIES.includes(data.category))
      ? data.category
      : ((dish?.category && CATEGORIES.includes(dish.category)) ? dish.category : 'lunch');
    const cleanedPreparation = (data.preparation || [])
      .filter(step => (step.instruction || '').trim())
      .map((step, index) => ({ step_number: index + 1, instruction: step.instruction.trim() }));
    return {
      name: data.name || dish?.name || '',
      description: data.description || '',
      image: '',
      categories: [cat],
      category: cat,
      food_category: '',
      max_repetitions: 1,
      allowed_days: [],
      prep_time: data.prep_time ? parseInt(data.prep_time) : null,
      servings: (data.servings !== null && data.servings !== undefined && data.servings !== '') ? parseInt(data.servings) : null,
      calories: (data.calories !== null && data.calories !== undefined && data.calories !== '') ? parseInt(data.calories) : null,
      protein_g: data.protein_g ?? null,
      carbs_g: data.carbs_g ?? null,
      fat_g: data.fat_g ?? null,
      fiber_g: data.fiber_g ?? null,
      ingredients: (data.ingredients || []).filter(ing => (ing.name || '').trim()),
      preparation: cleanedPreparation,
    };
  };

  // Generate + save each selected dish directly, then go to the recipe list
  const handleCreateSelected = async () => {
    const ingredients = ingredientList.map(i => i.trim()).filter(Boolean);
    const dishes = selectedSuggestions
      .slice()
      .sort((a, b) => a - b)
      .map(i => suggestions[i])
      .filter(Boolean);
    if (dishes.length === 0) return;

    setCreatingMultiple(true);
    setBulkProgress({ done: 0, total: dishes.length });
    let created = 0;
    try {
      for (const dish of dishes) {
        try {
          const genRes = await fetch(`${API}/recipes/generate-from-ingredients`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ingredients, dish_name: dish?.name, meal_type: dish?.category }),
          });
          if (!genRes.ok) { setBulkProgress(p => ({ ...p, done: p.done + 1 })); continue; }
          const data = await genRes.json();
          const payload = buildRecipePayloadFromGenerated(data, dish);
          const saveRes = await fetch(`${API}/recipes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
          });
          if (saveRes.ok) created += 1;
        } catch { /* skip this dish, keep going */ }
        setBulkProgress(p => ({ ...p, done: p.done + 1 }));
      }

      if (created > 0) {
        toast.success(language === 'es'
          ? `${created} receta${created > 1 ? 's' : ''} creada${created > 1 ? 's' : ''}`
          : `${created} recipe${created > 1 ? 's' : ''} created`);
        setShowIngredientsDialog(false);
        setSuggestions([]);
        setSelectedSuggestions([]);
        setIngredientList(['']);
        navigate('/recipes');
      } else {
        toast.error(t('recipes.fromIngredients.error'));
      }
    } finally {
      setCreatingMultiple(false);
      setBulkProgress({ done: 0, total: 0 });
    }
  };

  const handleEstimateMacros = async () => {
    if (!isEdit) return;
    setEstimatingMacros(true);
    try {
      const response = await fetch(`${API}/recipes/${recipeId}/estimate-macros`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          calories: data.calories ?? prev.calories,
          protein_g: data.protein_g ?? '',
          carbs_g: data.carbs_g ?? '',
          fat_g: data.fat_g ?? '',
          fiber_g: data.fiber_g ?? '',
        }));
        toast.success(language === 'es' ? 'Macros estimados con IA' : 'Macros estimated with AI');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setEstimatingMacros(false);
    }
  };

  // Recipe Import from Audio
  const handleImportFromAudio = async (fileOrBlob) => {
    setImportingAudio(true);
    const payload = new FormData();
    payload.append('file', fileOrBlob, fileOrBlob.name || 'recording.webm');
    try {
      const response = await fetch(`${API}/recipes/import-audio`, {
        method: 'POST',
        credentials: 'include',
        body: payload,
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          name: data.name || '',
          description: data.description || '',
          prep_time: data.prep_time || '',
          servings: data.servings ?? prev.servings,
          ingredients: data.ingredients || [],
          preparation: data.preparation || [],
          calories: data.calories || '',
          category: data.category || prev.category,
        }));
        setShowAudioDialog(false);
        toast.success(language === 'es' ? 'Receta importada correctamente' : 'Recipe imported successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setImportingAudio(false);
    }
  };

  const startRecording = async () => {
    setMicError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(s => {
          if (s >= 300) { stopRecording(); return s; }
          return s + 1;
        });
      }, 1000);
    } catch {
      setMicError(language === 'es' ? 'No se pudo acceder al micrófono. Comprueba los permisos.' : 'Could not access microphone. Check permissions.');
    }
  };

  const stopRecording = () => {
    clearInterval(recordingTimerRef.current);
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      mediaRecorderRef.current?.stream?.getTracks().forEach(t => t.stop());
      handleImportFromAudio(blob);
    };
    mediaRecorderRef.current.stop();
    setIsRecording(false);
  };

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // Staging queue for multi-page photo import
  const addPhotos = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setPhotoQueue(prev => [...prev, ...files.map(f => ({ file: f, url: URL.createObjectURL(f) }))]);
  };
  const removePhoto = (index) => {
    setPhotoQueue(prev => {
      const item = prev[index];
      if (item) { try { URL.revokeObjectURL(item.url); } catch { /* ignore */ } }
      return prev.filter((_, i) => i !== index);
    });
  };
  const clearPhotoQueue = () => {
    setPhotoQueue(prev => { prev.forEach(p => { try { URL.revokeObjectURL(p.url); } catch { /* ignore */ } }); return []; });
  };

  // Recipe Import from Photo (supports multiple pages of the same recipe)
  const handleImportFromPhoto = async (fileList, source) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    setPhotoSource(source || 'upload');
    setImportingPhoto(true);
    const payload = new FormData();
    files.forEach((f, i) => payload.append('files', f, f.name || `photo_${i}.jpg`));
    try {
      const response = await fetch(`${API}/recipes/import-image`, {
        method: 'POST',
        credentials: 'include',
        body: payload,
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          name: data.name || '',
          description: data.description || '',
          prep_time: data.prep_time || '',
          servings: data.servings ?? prev.servings,
          ingredients: data.ingredients || [],
          preparation: data.preparation || [],
          calories: data.calories || '',
          category: data.category || prev.category,
        }));
        setShowPhotoDialog(false);
        clearPhotoQueue();
        toast.success(language === 'es' ? 'Receta importada correctamente' : 'Recipe imported successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setImportingPhoto(false);
      setPhotoSource(null);
    }
  };

  // Ingredient handlers
  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { name: '', quantity: '' }],
    });
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const removeIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index),
    });
  };

  // Preparation step handlers
  const addStep = () => {
    const nextNumber = formData.preparation.length + 1;
    setFormData({
      ...formData,
      preparation: [...formData.preparation, { step_number: nextNumber, instruction: '' }],
    });
  };

  const updateStep = (index, instruction) => {
    const newPreparation = [...formData.preparation];
    newPreparation[index] = { ...newPreparation[index], instruction };
    setFormData({ ...formData, preparation: newPreparation });
  };

  const removeStep = (index) => {
    const newPreparation = formData.preparation
      .filter((_, i) => i !== index)
      .map((step, i) => ({ ...step, step_number: i + 1 }));
    setFormData({ ...formData, preparation: newPreparation });
  };



  const toggleCategory = (cat) => {
    const newCategories = [...formData.categories];
    let updatedCategories;
    if (newCategories.includes(cat)) {
      updatedCategories = newCategories.filter(c => c !== cat);
    } else {
      updatedCategories = [...newCategories, cat];
    }

    setFormData({
      ...formData,
      categories: updatedCategories,
      category: updatedCategories.length > 0 ? updatedCategories[0] : ''
    });
  };

  const toggleDay = (dayIndex) => {
    const newDays = [...formData.allowed_days];
    if (newDays.includes(dayIndex)) {
      setFormData({
        ...formData,
        allowed_days: newDays.filter(d => d !== dayIndex)
      });
    } else {
      setFormData({
        ...formData,
        allowed_days: [...newDays, dayIndex].sort()
      });
    }
  };

  const handleConstraintTypeChange = (type) => {
    setConstraintType(type);
    if (type === 'max_repetitions') {
      // Restore the previously saved max_repetitions value
      setFormData({ ...formData, allowed_days: [], max_repetitions: savedMaxRep });
    } else {
      // Save current value before switching, then set to 7 (no limit) so day selection is the only constraint
      setSavedMaxRep(formData.max_repetitions);
      setFormData({ ...formData, max_repetitions: 7 });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-2xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/recipes')}
          className="mb-6 text-stone hover:text-charcoal -ml-2"
          data-testid="back-to-recipes-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
          {t('back')}
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-3xl text-charcoal mb-4">
            {isEdit ? t('edit') : t('create')} {t('nav.recipes').toLowerCase()}
          </h1>

          {!isEdit && user?.plan === 'premium' && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => setShowIngredientsDialog(true)}
                className="rounded-full border-primary text-primary hover:bg-primary/5"
                data-testid="show-ingredients-dialog-button"
              >
                <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {t('recipes.fromIngredients.button')}
              </Button>
              {adminConfig?.recipe_import_enabled && (
                <Button
                  variant="outline"
                  onClick={() => setShowImportDialog(true)}
                  className="rounded-full border-primary text-primary hover:bg-primary/5"
                  data-testid="show-import-dialog-button"
                >
                  <Link className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {language === 'es' ? 'Desde URL' : 'From URL'}
                </Button>
              )}
              {adminConfig?.recipe_audio_import_enabled && (
                <Button
                  variant="outline"
                  onClick={() => { setAudioMode('upload'); setMicError(''); setShowAudioDialog(true); }}
                  className="rounded-full border-primary text-primary hover:bg-primary/5"
                  data-testid="show-audio-import-button"
                >
                  <Mic className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {language === 'es' ? 'Desde audio' : 'From audio'}
                </Button>
              )}
              {adminConfig?.recipe_image_import_enabled && (
                <Button
                  variant="outline"
                  onClick={() => setShowPhotoDialog(true)}
                  className="rounded-full border-primary text-primary hover:bg-primary/5"
                  data-testid="show-photo-import-button"
                >
                  <Camera className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {language === 'es' ? 'Desde foto' : 'From photo'}
                </Button>
              )}
              {adminConfig?.recipe_video_import_enabled && (
                <Button
                  variant="outline"
                  onClick={() => setShowVideoDialog(true)}
                  className="rounded-full border-primary text-primary hover:bg-primary/5"
                  data-testid="show-video-import-button"
                >
                  <Video className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {language === 'es' ? 'Desde vídeo' : 'From video'}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {language === 'es' ? 'Importar receta desde URL' : 'Import recipe from URL'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {language === 'es'
                  ? 'Esta herramienta permite importar los detalles de una receta automáticamente pegando un enlace.'
                  : 'This tool allows you to automatically import recipe details by pasting a link.'}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p className="text-sm text-stone font-inter">
                {language === 'es'
                  ? 'Pega el enlace de una página de recetas y la IA extraerá los detalles automáticamente.'
                  : 'Paste a link to a recipe page and AI will extract the details automatically.'}
              </p>
              <Input
                value={importUrlValue}
                onChange={(e) => setImportUrlValue(e.target.value)}
                placeholder="https://..."
                className="h-12 rounded-xl"
                autoFocus
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowImportDialog(false)}
                className="rounded-full"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleImportFromUrl}
                disabled={!importUrlValue.trim() || importingUrl}
                className="rounded-full bg-primary"
              >
                {importingUrl ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {language === 'es' ? 'Importar' : 'Import'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Audio Import Dialog */}
        <Dialog open={showAudioDialog} onOpenChange={(open) => { if (!isRecording) { setShowAudioDialog(open); if (!open) { setMicError(''); setRecordingSeconds(0); } } }}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {language === 'es' ? 'Importar receta desde audio' : 'Import recipe from audio'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {language === 'es' ? 'Sube un audio o graba tu voz describiendo la receta.' : 'Upload an audio file or record your voice describing the recipe.'}
              </DialogDescription>
            </DialogHeader>

            {/* Mode tabs */}
            <div className="flex gap-2 p-1 bg-muted rounded-xl mt-1">
              <button
                type="button"
                onClick={() => setAudioMode('upload')}
                className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors ${audioMode === 'upload' ? 'bg-white shadow-sm text-charcoal' : 'text-stone hover:text-charcoal'}`}
              >
                {language === 'es' ? 'Subir archivo' : 'Upload file'}
              </button>
              <button
                type="button"
                onClick={() => setAudioMode('record')}
                className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors ${audioMode === 'record' ? 'bg-white shadow-sm text-charcoal' : 'text-stone hover:text-charcoal'}`}
              >
                {language === 'es' ? 'Grabar audio' : 'Record audio'}
              </button>
            </div>

            <div className="py-2 space-y-4">
              {audioMode === 'upload' ? (
                <>
                  <p className="text-sm text-stone font-inter">
                    {language === 'es'
                      ? 'Sube un audio donde describas la receta y la IA extraerá los datos automáticamente.'
                      : 'Upload an audio where you describe the recipe and AI will extract the details automatically.'}
                  </p>
                  <input
                    ref={audioFileInputRef}
                    type="file"
                    accept="audio/*,.mp3,.mp4,.m4a,.wav,.ogg,.webm"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFromAudio(f); }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => audioFileInputRef.current?.click()}
                    disabled={importingAudio}
                    className="w-full rounded-xl h-20 border-dashed flex flex-col gap-1"
                  >
                    {importingAudio ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">{language === 'es' ? 'Analizando...' : 'Analyzing...'}</span></>
                    ) : (
                      <><Upload className="w-5 h-5" /><span className="text-sm">{language === 'es' ? 'Seleccionar archivo de audio' : 'Select audio file'}</span></>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-stone font-inter">
                    {language === 'es'
                      ? 'Graba tu voz describiendo la receta: ingredientes, pasos y nombre.'
                      : 'Record your voice describing the recipe: ingredients, steps, and name.'}
                  </p>
                  {micError && <p className="text-sm text-red-500">{micError}</p>}
                  <div className="flex flex-col items-center gap-4 py-2">
                    {isRecording ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-sm font-medium text-charcoal">{fmtTime(recordingSeconds)}</span>
                        </div>
                        <Button type="button" onClick={stopRecording} className="rounded-full bg-red-500 hover:bg-red-600 text-white">
                          {language === 'es' ? 'Detener y analizar' : 'Stop & analyze'}
                        </Button>
                      </>
                    ) : (
                      <Button type="button" onClick={startRecording} disabled={importingAudio} className="rounded-full bg-primary">
                        {importingAudio ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{language === 'es' ? 'Analizando...' : 'Analyzing...'}</>
                        ) : (
                          <><Mic className="w-4 h-4 mr-2" />{language === 'es' ? 'Empezar a grabar' : 'Start recording'}</>
                        )}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { if (!isRecording) setShowAudioDialog(false); }}
                disabled={isRecording || importingAudio}
                className="rounded-full"
              >
                {t('cancel')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Photo Import Dialog */}
        <Dialog open={showPhotoDialog} onOpenChange={(open) => { setShowPhotoDialog(open); if (!open) clearPhotoQueue(); }}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {language === 'es' ? 'Importar receta desde foto' : 'Import recipe from photo'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {language === 'es' ? 'Añade una o varias fotos de la receta y analízalas juntas.' : 'Add one or more photos of the recipe and analyze them together.'}
              </DialogDescription>
            </DialogHeader>

            <div className="py-2 space-y-4">
              <p className="text-sm text-stone font-inter">
                {language === 'es'
                  ? 'Añade las fotos de la receta (libro, revista, pantalla). Si ocupa varias páginas, haz una foto tras otra o añádelas de la galería, y cuando tengas todas pulsa “Analizar”.'
                  : 'Add the recipe photos (book, magazine, screen). If it spans several pages, take them one after another or add them from the gallery, then press “Analyze”.'}
              </p>

              <input
                ref={photoFileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
              />
              <input
                ref={photoCaptureInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
              />

              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => photoCaptureInputRef.current?.click()}
                  disabled={importingPhoto}
                  className="rounded-xl h-20 flex flex-col gap-1 border-dashed"
                >
                  <Camera className="w-5 h-5" /><span className="text-xs">{language === 'es' ? 'Hacer foto' : 'Take photo'}</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => photoFileInputRef.current?.click()}
                  disabled={importingPhoto}
                  className="rounded-xl h-20 flex flex-col gap-1 border-dashed"
                >
                  <Upload className="w-5 h-5" /><span className="text-xs">{language === 'es' ? 'De galería' : 'From gallery'}</span>
                </Button>
              </div>

              {photoQueue.length > 0 && (
                <div>
                  <p className="text-xs text-stone font-inter mb-2">
                    {photoQueue.length} {language === 'es' ? (photoQueue.length === 1 ? 'foto añadida' : 'fotos añadidas') : (photoQueue.length === 1 ? 'photo added' : 'photos added')}
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {photoQueue.map((p, i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted">
                        <img src={p.url} alt="" className="w-full h-full object-cover" />
                        <span className="absolute bottom-0.5 left-0.5 text-[10px] bg-black/60 text-white rounded px-1">{i + 1}</span>
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          disabled={importingPhoto}
                          className="absolute top-0.5 right-0.5 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPhotoDialog(false)}
                disabled={importingPhoto}
                className="rounded-full"
              >
                {t('cancel')}
              </Button>
              <Button
                type="button"
                onClick={() => handleImportFromPhoto(photoQueue.map(p => p.file), 'analyze')}
                disabled={importingPhoto || photoQueue.length === 0}
                className="rounded-full bg-primary"
              >
                {importingPhoto ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{language === 'es' ? 'Analizando...' : 'Analyzing...'}</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />{language === 'es' ? `Analizar ${photoQueue.length || ''} ${photoQueue.length === 1 ? 'foto' : 'fotos'}`.trim() : `Analyze ${photoQueue.length || ''} ${photoQueue.length === 1 ? 'photo' : 'photos'}`.trim()}</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Video Import Dialog */}
        <Dialog open={showVideoDialog} onOpenChange={(open) => { setShowVideoDialog(open); if (!open) setImportVideoValue(''); }}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {language === 'es' ? 'Importar receta desde vídeo' : 'Import recipe from video'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {language === 'es'
                  ? 'Pega un enlace de YouTube y la IA extraerá los datos de la receta automáticamente.'
                  : 'Paste a YouTube link and AI will extract the recipe details automatically.'}
              </DialogDescription>
            </DialogHeader>

            <div className="py-2 space-y-4">
              <p className="text-sm text-stone font-inter">
                {language === 'es'
                  ? 'Pega el enlace de un vídeo de YouTube con una receta y la IA extraerá los datos automáticamente desde los subtítulos.'
                  : 'Paste a YouTube recipe video link and AI will automatically extract the details from the transcript.'}
              </p>
              <Input
                value={importVideoValue}
                onChange={(e) => setImportVideoValue(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="h-12 rounded-xl"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleImportFromVideo(); }}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowVideoDialog(false); setImportVideoValue(''); }}
                className="rounded-full"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleImportFromVideo}
                disabled={!importVideoValue.trim() || importingVideo}
                className="rounded-full bg-primary"
              >
                {importingVideo ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {importingVideo
                  ? (language === 'es' ? 'Importando...' : 'Importing...')
                  : (language === 'es' ? 'Importar' : 'Import')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Generate-from-ingredients Dialog (two-step) */}
        <Dialog open={showIngredientsDialog} onOpenChange={(open) => { setShowIngredientsDialog(open); if (!open) { setSuggestions([]); } }}>
          <DialogContent className="rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-manrope">{t('recipes.fromIngredients.title')}</DialogTitle>
              <DialogDescription>{t('recipes.fromIngredients.subtitle')}</DialogDescription>
            </DialogHeader>

            {suggestions.length === 0 ? (
              <div className="space-y-3">
                <Label className="font-manrope font-semibold text-charcoal">
                  {t('recipes.fromIngredients.ingredientsLabel')}
                </Label>
                {ingredientList.map((ing, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={ing}
                      onChange={(e) => updateIngredientItem(i, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addIngredientItem(); } }}
                      placeholder={t('recipes.fromIngredients.ingredientPlaceholder')}
                      className="h-11 rounded-xl border-border bg-white/50 font-inter"
                      data-testid={`ingredient-input-${i}`}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeIngredientItem(i)} className="rounded-full text-stone hover:text-destructive shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addIngredientItem} className="rounded-full w-full border-dashed">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('recipes.fromIngredients.addIngredient')}
                </Button>
                <DialogFooter>
                  <Button
                    onClick={handleSuggestFromIngredients}
                    disabled={suggesting}
                    className="rounded-full bg-primary w-full"
                    data-testid="suggest-ingredients-button"
                  >
                    {suggesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {suggesting ? t('recipes.fromIngredients.suggesting') : t('recipes.fromIngredients.suggest')}
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-3">
                <Label className="font-manrope font-semibold text-charcoal">
                  {t('recipes.fromIngredients.suggestionsTitle')}
                </Label>
                <p className="text-xs text-stone/80 font-inter -mt-1">
                  {language === 'es'
                    ? 'Marca las que quieras y pulsa «Añadir», o pulsa «Crear esta receta» para revisar una antes de guardarla.'
                    : 'Tick the ones you want and press “Add”, or press “Create this recipe” to review one before saving.'}
                </p>
                {suggestions.map((s, i) => {
                  const isSel = selectedSuggestions.includes(i);
                  return (
                  <div
                    key={i}
                    onClick={() => !creatingMultiple && toggleSuggestionSelected(i)}
                    className={`border rounded-2xl p-4 space-y-2 cursor-pointer transition-all ${isSel ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'border-border hover:border-primary/30'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3">
                        <span className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${isSel ? 'bg-primary border-primary text-white' : 'border-border bg-white'}`}>
                          {isSel && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                        </span>
                        <div>
                          <p className="font-manrope font-semibold text-charcoal">{s.name}</p>
                          {s.category && (
                            <span className="text-xs text-primary">{t(`recipes.categories.${s.category}`)}</span>
                          )}
                        </div>
                      </div>
                      {typeof s.est_calories === 'number' && (
                        <span className="text-sm text-stone whitespace-nowrap">{s.est_calories} kcal</span>
                      )}
                    </div>
                    {s.description && <p className="text-sm text-stone font-inter">{s.description}</p>}
                    {Array.isArray(s.uses) && s.uses.length > 0 && (
                      <p className="text-xs text-sage"><b>{t('recipes.fromIngredients.uses')}:</b> {s.uses.join(', ')}</p>
                    )}
                    {Array.isArray(s.missing) && s.missing.length > 0 && (
                      <p className="text-xs text-stone/80"><b>{t('recipes.fromIngredients.missing')}:</b> {s.missing.join(', ')}</p>
                    )}
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleGenerateFromIngredients(s); }}
                      disabled={generatingFromIng || creatingMultiple}
                      variant="outline"
                      className="rounded-full border-primary text-primary hover:bg-primary/5 w-full mt-1"
                      data-testid={`generate-recipe-button-${i}`}
                    >
                      {generatingFromIng ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      {generatingFromIng ? t('recipes.fromIngredients.generating') : t('recipes.fromIngredients.generate')}
                    </Button>
                  </div>
                  );
                })}

                {selectedSuggestions.length > 0 && (
                  <Button
                    onClick={handleCreateSelected}
                    disabled={creatingMultiple || generatingFromIng}
                    className="rounded-full bg-primary w-full"
                    data-testid="create-selected-recipes-button"
                  >
                    {creatingMultiple ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    {creatingMultiple
                      ? (language === 'es'
                        ? `Creando ${bulkProgress.done}/${bulkProgress.total}...`
                        : `Creating ${bulkProgress.done}/${bulkProgress.total}...`)
                      : (language === 'es'
                        ? `Añadir ${selectedSuggestions.length} receta${selectedSuggestions.length > 1 ? 's' : ''}`
                        : `Add ${selectedSuggestions.length} recipe${selectedSuggestions.length > 1 ? 's' : ''}`)}
                  </Button>
                )}
                {selectedSuggestions.length > 0 && !creatingMultiple && (
                  <p className="text-xs text-stone/70 font-inter text-center -mt-1">
                    {language === 'es'
                      ? 'Se crearán y guardarán directamente, sin pasar por el formulario.'
                      : 'They will be created and saved directly, without going through the form.'}
                  </p>
                )}

                <Button type="button" variant="outline" onClick={() => { setSuggestions([]); setSelectedSuggestions([]); }} disabled={creatingMultiple} className="rounded-full w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('back')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card className="bg-white rounded-3xl shadow-soft p-6 md:p-8 space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="font-manrope font-semibold text-charcoal">
                {t('recipes.form.name')} *
              </Label>
              <Input
                id="name"
                data-testid="recipe-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('recipes.form.namePlaceholder')}
                className="h-12 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="font-manrope font-semibold text-charcoal">
                {t('recipes.form.description')}
              </Label>
              <Textarea
                id="description"
                data-testid="recipe-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('recipes.form.descriptionPlaceholder')}
                rows={3}
                className="rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter resize-none"
              />
            </div>

            {/* Category */}
            {/* Category Selection */}
            <div className="space-y-3">
              <Label className="font-manrope font-semibold text-charcoal">
                {t('recipes.form.category')} *
              </Label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className={`px-4 py-2.5 rounded-full font-manrope font-semibold text-sm border-2 transition-all ${formData.categories.includes(cat)
                      ? 'bg-primary border-primary text-white shadow-md'
                      : 'bg-white border-border text-stone hover:border-primary/30'
                      }`}
                  >
                    {t(`recipes.categories.${cat}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Food Category */}
            {foodCategories.length > 0 && (
              <div className="space-y-2">
                <Label className="font-manrope font-semibold text-charcoal">
                  {language === 'es' ? 'Tipo de alimento' : 'Food type'}
                </Label>
                <p className="text-xs font-inter text-stone">
                  {language === 'es'
                    ? 'El menú evitará poner el mismo tipo de alimento en días consecutivos.'
                    : 'The menu will avoid placing the same food type on consecutive days.'}
                </p>
                <Select
                  value={formData.food_category || 'none'}
                  onValueChange={val => setFormData({ ...formData, food_category: val === 'none' ? '' : val })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={language === 'es' ? 'Sin categoría' : 'No category'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{language === 'es' ? 'Sin categoría' : 'No category'}</SelectItem>
                    {foodCategories.map(cat => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>
                        {language === 'es' ? cat.name_es : cat.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Max Repetitions & Prep Time */}
            <div className="space-y-4 pt-2">
              <Label className="font-manrope font-semibold text-charcoal">
                {t('recipes.form.generationConstraint')}
              </Label>

              <div className="flex gap-2 p-1 bg-stone/5 rounded-2xl w-full">
                <button
                  type="button"
                  onClick={() => handleConstraintTypeChange('max_repetitions')}
                  className={`flex-1 py-2.5 px-4 rounded-xl font-manrope font-bold text-sm transition-all ${constraintType === 'max_repetitions'
                    ? 'bg-white shadow-soft text-primary'
                    : 'text-stone hover:text-charcoal'
                    }`}
                >
                  {t('recipes.form.maxRepetitionsType')}
                </button>
                <button
                  type="button"
                  onClick={() => handleConstraintTypeChange('specific_days')}
                  className={`flex-1 py-2.5 px-4 rounded-xl font-manrope font-bold text-sm transition-all ${constraintType === 'specific_days'
                    ? 'bg-white shadow-soft text-primary'
                    : 'text-stone hover:text-charcoal'
                    }`}
                >
                  {t('recipes.form.specificDaysType')}
                </button>
              </div>

              {constraintType === 'max_repetitions' ? (
                <div className="space-y-2 animate-fade-in">
                  <Label htmlFor="max_repetitions" className="font-inter text-sm text-stone">
                    {t('recipes.form.maxRepetitions')}
                  </Label>
                  <Input
                    id="max_repetitions"
                    data-testid="recipe-max-repetitions-input"
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="7"
                    value={formData.max_repetitions}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        setFormData({ ...formData, max_repetitions: '' });
                      } else {
                        const num = parseInt(raw);
                        if (!isNaN(num)) setFormData({ ...formData, max_repetitions: Math.min(7, Math.max(1, num)) });
                      }
                    }}
                    onBlur={() => {
                      if (!formData.max_repetitions || formData.max_repetitions < 1) {
                        setFormData({ ...formData, max_repetitions: 1 });
                      }
                    }}
                    className="h-12 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
                  />
                  <p className="text-xs text-stone/70 font-inter">
                    {language === 'es'
                      ? 'El generador podrá incluir esta receta hasta este número de veces por semana, en cualquier día.'
                      : 'The generator can include this recipe up to this many times per week, on any day.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3 animate-fade-in">
                  <Label className="font-inter text-sm text-stone">
                    {t('recipes.form.allowedDays')}
                  </Label>
                  <p className="text-xs text-stone/70 font-inter -mt-1">
                    {language === 'es'
                      ? 'El generador solo asignará esta receta en los días seleccionados.'
                      : 'The generator will only assign this recipe on the selected days.'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, index) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(index)}
                        className={`px-4 py-2 rounded-full font-manrope font-semibold text-sm border-2 transition-all ${formData.allowed_days.includes(index)
                          ? 'bg-primary border-primary text-white shadow-md'
                          : 'bg-white border-border text-stone hover:border-stone/30'
                          }`}
                      >
                        {t(`daysShort.${day}`)}
                      </button>
                    ))}
                  </div>
                  {formData.allowed_days.length === 0 && (
                    <p className="text-xs text-destructive italic">
                      {language === 'es' ? '* Selecciona al menos un día' : '* Select at least one day'}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="prep_time" className="font-manrope font-semibold text-charcoal">
                  {t('recipes.form.prepTime')}
                </Label>
                <Input
                  id="prep_time"
                  data-testid="recipe-prep-time-input"
                  type="number"
                  min="1"
                  value={formData.prep_time}
                  onChange={(e) => setFormData({ ...formData, prep_time: e.target.value })}
                  className="h-12 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="servings" className="font-manrope font-semibold text-charcoal">
                  {language === 'es' ? 'Raciones' : 'Servings'}
                </Label>
                <Input
                  id="servings"
                  data-testid="recipe-servings-input"
                  type="number"
                  min="1"
                  value={formData.servings}
                  onChange={(e) => setFormData({ ...formData, servings: e.target.value })}
                  placeholder={language === 'es' ? 'Ej: 4 personas' : 'E.g.: 4 people'}
                  className="h-12 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
                />
              </div>
            </div>

            {/* Calories (Manual or AI) */}
            <div className="space-y-2">
              <Label htmlFor="calories" className="font-manrope font-semibold text-charcoal">
                {language === 'es' ? 'Calorías por ración' : 'Calories per serving'}
              </Label>
              <Input
                id="calories"
                data-testid="recipe-calories-input"
                type="number"
                min="0"
                value={formData.calories}
                onChange={(e) => setFormData({ ...formData, calories: e.target.value })}
                placeholder={language === 'es' ? 'Ej: 350' : 'E.g.: 350'}
                className="h-12 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
              />
              {adminConfig?.calorie_enabled && (
                <p className="text-sm text-stone/80 font-inter flex items-start gap-2 mt-1">
                  <span className="text-sage">💡</span>
                  <span>
                    {user?.plan === 'free'
                      ? (language === 'es'
                        ? 'Introduce las calorías manualmente. La estimación automática con IA está disponible en el plan Premium.'
                        : 'Enter calories manually. Automatic AI estimation is available on the Premium plan.')
                      : (language === 'es'
                        ? 'Deja en blanco para que la IA estime las calorías automáticamente. Introduce un valor para usar tu propio cálculo.'
                        : 'Leave blank to have calories estimated automatically by AI. Enter a value to override and prevent AI calculation.')}
                  </span>
                </p>
              )}
            </div>

            {/* Macros (per serving) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="font-manrope font-semibold text-charcoal">
                  {t('recipes.nutrition.title')}
                </Label>
                {isEdit && user?.plan === 'premium' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEstimateMacros}
                    disabled={estimatingMacros}
                    className="rounded-full border-primary text-primary hover:bg-primary/5"
                    data-testid="estimate-macros-button"
                  >
                    {estimatingMacros ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {estimatingMacros ? t('recipes.nutrition.estimating') : t('recipes.nutrition.estimate')}
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { key: 'protein_g', label: t('recipes.nutrition.protein') },
                  { key: 'carbs_g', label: t('recipes.nutrition.carbs') },
                  { key: 'fat_g', label: t('recipes.nutrition.fat') },
                  { key: 'fiber_g', label: t('recipes.nutrition.fiber') },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-sm text-stone font-inter">{label} (g)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData[key]}
                      onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                      placeholder="0"
                      className="h-11 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
                      data-testid={`recipe-${key}-input`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Image Upload/URL */}
            <div className="space-y-3">
              <Label className="font-manrope font-semibold text-charcoal">
                {t('recipes.form.image')}
              </Label>

              {/* Image mode toggle */}
              <div className="flex gap-2 mb-3">
                <Button
                  type="button"
                  variant={imageMode === 'upload' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setImageMode('upload')}
                  className="rounded-full"
                >
                  <Upload className="w-4 h-4 mr-1" strokeWidth={1.5} />
                  {language === 'es' ? 'Subir' : 'Upload'}
                </Button>
                <Button
                  type="button"
                  variant={imageMode === 'url' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setImageMode('url')}
                  className="rounded-full"
                >
                  <Link className="w-4 h-4 mr-1" strokeWidth={1.5} />
                  URL
                </Button>
              </div>

              {imageMode === 'url' ? (
                <Input
                  id="image"
                  data-testid="recipe-image-input"
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder={t('recipes.form.imagePlaceholder')}
                  className="h-12 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
                />
              ) : (
                <div className="space-y-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="recipe-image-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="w-full h-24 rounded-xl border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center gap-2"
                    data-testid="upload-image-button"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        <span className="font-inter text-sm text-stone">
                          {language === 'es' ? 'Subiendo...' : 'Uploading...'}
                        </span>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-6 h-6 text-stone" strokeWidth={1.5} />
                        <span className="font-inter text-sm text-stone">
                          {language === 'es' ? 'Clic para seleccionar imagen' : 'Click to select image'}
                        </span>
                        <span className="font-inter text-xs text-stone/60">
                          JPEG, PNG, WebP, GIF - Max 5MB
                        </span>
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* AI image generation hint for Premium users */}
              {!formData.image && user?.plan === 'premium' && adminConfig?.pexels_enabled && (
                <p className="text-xs text-primary/70 font-inter flex items-center gap-1.5">
                  <span>✨</span>
                  {language === 'es'
                    ? 'Si no subes ninguna imagen, la IA generará una automáticamente al guardar.'
                    : 'If you don\'t upload an image, AI will generate one automatically when saving.'}
                </p>
              )}

              {/* Image preview */}
              {formData.image && (
                <div className="mt-3 relative">
                  <img
                    key={formData.image}
                    src={formData.image}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-xl"
                    onError={(e) => {
                      if (formData.image) {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 w-8 h-8 rounded-full"
                    onClick={() => setFormData({ ...formData, image: '' })}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              )}
            </div>

            {/* Ingredients */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="font-manrope font-semibold text-charcoal">
                  {t('recipes.form.ingredients')}
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={addIngredient}
                  className="text-primary hover:text-primary/80"
                  data-testid="add-ingredient-button"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />
                  {t('recipes.form.addIngredient')}
                </Button>
              </div>

              {formData.ingredients.length === 0 && (
                <p className="text-sm text-stone/70 font-inter italic">
                  {language === 'es'
                    ? 'Sin ingredientes. Las calorías se estimarán según el nombre del plato.'
                    : 'No ingredients. Calories will be estimated based on dish name.'}
                </p>
              )}

              {formData.ingredients.map((ingredient, index) => (
                <div key={index} className="flex gap-2 items-start animate-fade-in">
                  <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_150px] gap-2">
                    <Input
                      data-testid={`ingredient-name-${index}`}
                      value={ingredient.name}
                      onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                      placeholder={t('recipes.form.ingredientName')}
                      className="h-11 w-full rounded-xl border-border bg-white/50 font-inter"
                    />
                    <Input
                      data-testid={`ingredient-quantity-${index}`}
                      value={ingredient.quantity}
                      onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                      placeholder={t('recipes.form.ingredientQuantity')}
                      className="h-11 w-full rounded-xl border-border bg-white/50 font-inter"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeIngredient(index)}
                    className="h-11 w-10 shrink-0 text-stone hover:text-destructive"
                    data-testid={`remove-ingredient-${index}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              ))}
            </div>

            {/* Preparation Steps */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="font-manrope font-semibold text-charcoal">
                  {language === 'es' ? 'Preparación' : 'Preparation'}
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={addStep}
                  className="text-primary hover:text-primary/80"
                  data-testid="add-step-button"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />
                  {language === 'es' ? 'Añadir paso' : 'Add step'}
                </Button>
              </div>

              {formData.preparation.length === 0 && (
                <p className="text-sm text-stone/70 font-inter italic">
                  {language === 'es'
                    ? 'Sin pasos de preparación. Añade instrucciones paso a paso.'
                    : 'No preparation steps. Add step-by-step instructions.'}
                </p>
              )}

              {formData.preparation.map((step, index) => (
                <div key={index} className="flex gap-2 items-start animate-fade-in">
                  <div className="w-8 h-8 mt-1.5 flex-shrink-0 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="font-manrope font-bold text-primary text-sm">{index + 1}</span>
                  </div>
                  <Textarea
                    data-testid={`step-instruction-${index}`}
                    value={step.instruction}
                    onChange={(e) => updateStep(index, e.target.value)}
                    placeholder={language === 'es'
                      ? `Paso ${index + 1}: Describe la instrucción...`
                      : `Step ${index + 1}: Describe the instruction...`}
                    rows={3}
                    className="flex-1 min-w-0 rounded-xl border-border bg-white/50 font-inter resize-y"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeStep(index)}
                    className="h-11 w-10 shrink-0 text-stone hover:text-destructive"
                    data-testid={`remove-step-${index}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6 font-manrope font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
                data-testid="save-recipe-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {isEdit ? t('recipes.form.updating') : t('recipes.form.creating')}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    {t('save')}
                  </>
                )}
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </Layout>
  );
}
